import {
    ActionType,
    EntityType,
    SchemaJson
} from "@cedar-policy/cedar-wasm/nodejs";
import {
    PermissionEntityType,
    PermissionResourceType,
    PermissionType
} from "../../../models";

type AcceptedEntityType = keyof typeof PermissionEntityType | keyof typeof PermissionResourceType;
type AcceptedActionType = "VIEWER_ACTION_GROUP" | "EDITOR_ACTION_GROUP" | "ADMIN_ACTION_GROUP" | keyof typeof PermissionType;

export const schema: SchemaJson<AcceptedEntityType> = {
    "": {
        "entityTypes": {
            "USER": {
                "memberOfTypes": [
                    "TEAM"
                ]
            },
            "TEAM": {},
            "PROPERTY": {
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "LOCATION": {
                "memberOfTypes": [
                    "PROPERTY"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "ISSUE": {
                "memberOfTypes": [
                    "PROPERTY",
                    "LOCATION"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "DIMENSION": {
                "memberOfTypes": [
                    "PROPERTY",
                    "ISSUE"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "WORK_SPECIFICATION": {
                "memberOfTypes": [
                    "PROPERTY",
                    "ISSUE"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "ATTACHMENT": {
                "memberOfTypes": [
                    "PROPERTY",
                    "LOCATION",
                    "ISSUE",
                    "WORK_SPECIFICATION"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "SOLUTION": {
                "memberOfTypes": [
                    "PROPERTY"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            },
            "PROPOSAL": {
                "memberOfTypes": [
                    "PROPERTY",
                    "SOLUTION"
                ],
                "shape": {
                    "type": "Record",
                    "attributes": {
                        "createdBy": {
                            "type": "Entity",
                            "name": "USER",
                            "required": false
                        }
                    }
                }
            }
        } satisfies Partial<Record<AcceptedEntityType, EntityType<AcceptedEntityType>>>,
        "actions": {
            "VIEWER_ACTION_GROUP": {
                "memberOf": [
                    {
                        "id": "EDITOR_ACTION_GROUP"
                    }
                ]
            },
            "EDITOR_ACTION_GROUP": {
                "memberOf": [
                    {
                        "id": "ADMIN_ACTION_GROUP"
                    }
                ]
            },
            "ADMIN_ACTION_GROUP": {},
            "CREATE_CHILDREN": {
                "memberOf": [
                    {
                        "id": "EDITOR_ACTION_GROUP"
                    }
                ],
                "appliesTo": {
                    "principalTypes": [
                        "USER"
                    ],
                    "resourceTypes": [
                        "PROPERTY",
                        "LOCATION",
                        "ISSUE",
                        "DIMENSION",
                        "WORK_SPECIFICATION",
                        "ATTACHMENT",
                        "SOLUTION",
                        "PROPOSAL"
                    ]
                }
            },
            "VIEW": {
                "memberOf": [
                    {
                        "id": "VIEWER_ACTION_GROUP"
                    }
                ],
                "appliesTo": {
                    "principalTypes": [
                        "USER"
                    ],
                    "resourceTypes": [
                        "PROPERTY",
                        "LOCATION",
                        "ISSUE",
                        "DIMENSION",
                        "WORK_SPECIFICATION",
                        "ATTACHMENT",
                        "SOLUTION",
                        "PROPOSAL"
                    ]
                }
            },
            "EDIT": {
                "memberOf": [
                    {
                        "id": "EDITOR_ACTION_GROUP"
                    }
                ],
                "appliesTo": {
                    "principalTypes": [
                        "USER"
                    ],
                    "resourceTypes": [
                        "PROPERTY",
                        "LOCATION",
                        "ISSUE",
                        "DIMENSION",
                        "WORK_SPECIFICATION",
                        "ATTACHMENT",
                        "SOLUTION",
                        "PROPOSAL"
                    ]
                }
            },
            "DELETE": {
                "memberOf": [
                    {
                        "id": "EDITOR_ACTION_GROUP"
                    }
                ],
                "appliesTo": {
                    "principalTypes": [
                        "USER"
                    ],
                    "resourceTypes": [
                        "PROPERTY",
                        "LOCATION",
                        "ISSUE",
                        "DIMENSION",
                        "WORK_SPECIFICATION",
                        "ATTACHMENT",
                        "SOLUTION",
                        "PROPOSAL"
                    ]
                }
            },
            "SHARE_PERMISSION": {
                "memberOf": [
                    {
                        "id": "ADMIN_ACTION_GROUP"
                    }
                ],
                "appliesTo": {
                    "principalTypes": [
                        "USER"
                    ],
                    "resourceTypes": [
                        "PROPERTY",
                        "LOCATION",
                        "ISSUE",
                        "DIMENSION",
                        "WORK_SPECIFICATION",
                        "ATTACHMENT",
                        "SOLUTION",
                        "PROPOSAL"
                    ]
                }
            },
            "REMOVE_PERMISSION": {
                "memberOf": [
                    {
                        "id": "ADMIN_ACTION_GROUP"
                    }
                ],
                "appliesTo": {
                    "principalTypes": [
                        "USER"
                    ],
                    "resourceTypes": [
                        "PROPERTY",
                        "LOCATION",
                        "ISSUE",
                        "DIMENSION",
                        "WORK_SPECIFICATION",
                        "ATTACHMENT",
                        "SOLUTION",
                        "PROPOSAL"
                    ]
                }
            },
        } satisfies Partial<Record<AcceptedActionType, ActionType<AcceptedEntityType>>>,
    }
};